import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, FileSpreadsheet, FileText, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Search, X, GripVertical, ChevronDown, ChevronRight as ChevronRightIcon } from 'lucide-react';
import ImportDialog from '@/components/ImportDialog';

const typeLabels = {
  pos_sale: 'POS Satis', pos_refund: 'POS Iade', pos_complimentary: 'POS Ikram', pos_no_charge: 'POS Odenmez',
  purchase_in: 'Satin Alma Girisi',
  production_in: 'Uretim Girisi', production_out: 'Uretim Cikisi',
  transfer_in: 'Transfer Girisi', transfer_out: 'Transfer Cikisi',
  sale_out: 'Satis Cikisi',
  waste: 'Fire / Zayi', count_adjustment: 'Sayim Duzeltme', return: 'Iade'
};

const typeColors = {
  pos_sale: 'bg-[#3d6e4e]/10 text-[#3d6e4e]', pos_refund: 'bg-[#7a3535]/10 text-[#7a3535]',
  pos_complimentary: 'bg-[#7a6a2e]/10 text-[#7a6a2e]', pos_no_charge: 'bg-[#5a5a7a]/10 text-[#5a5a7a]',
  purchase_in: 'bg-[#3d6e4e]/10 text-[#3d6e4e]', production_in: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
  production_out: 'bg-[#7a3535]/10 text-[#7a3535]', transfer_in: 'bg-[#2e2d29]/10 text-[#2e2d29]',
  transfer_out: 'bg-[#2e2d29]/10 text-[#2e2d29]', sale_out: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
  waste: 'bg-[#7a3535]/10 text-[#7a3535]', count_adjustment: 'bg-[#7a6a2e]/10 text-[#7a6a2e]', return: 'bg-[#7a6a2e]/10 text-[#7a6a2e]'
};

// All available columns
const allColumns = [
  { key: 'created_at', label: 'Tarih', groupable: true, getValue: (m) => new Date(m.movement_date || m.created_at).toLocaleDateString('tr-TR'), getSort: (m) => m.movement_date || m.created_at },
  { key: 'movement_type', label: 'Hareket Tipi', groupable: true, getValue: (m) => typeLabels[m.movement_type] || m.movement_type },
  { key: 'product_code', label: 'Urun Kodu', groupable: false },
  { key: 'product_name', label: 'Urun Adi', groupable: true },
  { key: 'category_name', label: 'Kategori', groupable: true, getValue: (m) => m.category_name || '-' },
  { key: 'subcategory_name', label: 'Alt Kategori', groupable: true, getValue: (m) => m.subcategory_name || '-' },
  { key: 'quantity', label: 'Miktar', groupable: false, isNumeric: true },
  { key: 'unit', label: 'Birim', groupable: false },
  { key: 'unit_cost', label: 'B.Maliyet', groupable: false, isNumeric: true },
  { key: 'total_cost', label: 'T.Maliyet', groupable: false, isNumeric: true },
  { key: 'warehouse_name', label: 'Depo', groupable: true, getValue: (m) => m.warehouse_name || '-' },
  { key: 'notes', label: 'Notlar', groupable: false },
  { key: 'created_by_name', label: 'Kaydeden', groupable: false },
];

const stockImportColumns = [
  { key: 'product_code', label: 'Urun Kodu' },
  { key: 'product_name', label: 'Urun' },
  { key: 'warehouse_name', label: 'Depo' },
  { key: 'quantity', label: 'Miktar', editable: true, type: 'number' },
  { key: 'unit', label: 'Birim', editable: true },
];

export default function StockMovements() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, per_page: 25 });
  const [warehouses, setWarehouses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [showImport, setShowImport] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [whFilter, setWhFilter] = useState('all');
  const [catFilter, setCatFilter] = useState('all');
  const [subcatFilter, setSubcatFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Pagination & sort
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  // Grouping
  const [groupByColumns, setGroupByColumns] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [draggedColumn, setDraggedColumn] = useState(null);

  useEffect(() => {
    api.get('/warehouses').then(r => setWarehouses(r.data)).catch(() => {});
    api.get('/categories').then(r => setCategories(r.data)).catch(() => {});
  }, []);

  const parentCategories = categories.filter(c => !c.parent_id);
  const filteredSubcats = categories.filter(c => c.parent_id === catFilter);
  useEffect(() => { setSubcatFilter('all'); }, [catFilter]);

  const fetchMovements = useCallback(async () => {
    const params = { page, per_page: perPage, sort_by: sortBy, sort_dir: sortDir };
    if (typeFilter !== 'all') params.movement_type = typeFilter;
    if (whFilter !== 'all') params.warehouse_id = whFilter;
    if (catFilter !== 'all') params.category_id = catFilter;
    if (subcatFilter !== 'all') params.subcategory_id = subcatFilter;
    if (search) params.search = search;
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    try {
      const res = await api.get('/stock/movements', { params });
      setData(res.data);
    } catch (err) { console.error(err); }
  }, [page, perPage, sortBy, sortDir, typeFilter, whFilter, catFilter, subcatFilter, search, dateFrom, dateTo]);

  useEffect(() => { fetchMovements(); }, [fetchMovements]);
  useEffect(() => { setPage(1); }, [typeFilter, whFilter, catFilter, subcatFilter, search, dateFrom, dateTo, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortBy(col); setSortDir('desc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown size={10} className="ml-0.5 text-[#d0c4b0]" />;
    return sortDir === 'asc' ? <ArrowUp size={10} className="ml-0.5 text-[#1a1916]" /> : <ArrowDown size={10} className="ml-0.5 text-[#1a1916]" />;
  };

  // Columns to display (excluding grouped columns)
  const visibleColumns = useMemo(() => {
    return allColumns.filter(col => !groupByColumns.includes(col.key));
  }, [groupByColumns]);

  // Drag and drop handlers
  const handleDragStart = (e, columnKey) => {
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDropOnGroupArea = (e) => {
    e.preventDefault();
    if (draggedColumn && !groupByColumns.includes(draggedColumn)) {
      const colDef = allColumns.find(c => c.key === draggedColumn);
      if (colDef?.groupable) {
        setGroupByColumns([...groupByColumns, draggedColumn]);
        setExpandedGroups({});
      }
    }
    setDraggedColumn(null);
  };

  const removeGroupColumn = (col) => {
    setGroupByColumns(groupByColumns.filter(c => c !== col));
    setExpandedGroups({});
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  // Aggregate rows by remaining visible columns (excluding numeric ones)
  const aggregateRows = (items) => {
    // Key columns for aggregation (non-numeric visible columns except notes/created_by)
    const keyColumns = visibleColumns.filter(c => 
      !c.isNumeric && c.key !== 'notes' && c.key !== 'created_by_name'
    );
    
    const aggregated = {};
    
    items.forEach(item => {
      // Build a unique key from key columns
      const keyParts = keyColumns.map(col => {
        if (col.getValue) return col.getValue(item);
        return item[col.key] || '-';
      });
      const key = keyParts.join('|||');
      
      if (!aggregated[key]) {
        aggregated[key] = {
          ...item,
          quantity: 0,
          total_cost: 0,
          _count: 0,
          _keyParts: keyParts
        };
      }
      
      aggregated[key].quantity += (item.quantity || 0);
      aggregated[key].total_cost += Math.abs(item.total_cost || 0);
      aggregated[key]._count += 1;
    });
    
    return Object.values(aggregated);
  };

  // Group data by selected columns with aggregation
  const groupedData = useMemo(() => {
    if (groupByColumns.length === 0) return null;

    const buildGroups = (items, columnIndex, parentKey = '') => {
      if (columnIndex >= groupByColumns.length) {
        // At leaf level, aggregate the rows
        return aggregateRows(items);
      }

      const colKey = groupByColumns[columnIndex];
      const colDef = allColumns.find(c => c.key === colKey);
      const groups = {};

      items.forEach(item => {
        const value = colDef?.getValue ? colDef.getValue(item) : item[colKey] || '-';
        if (!groups[value]) groups[value] = [];
        groups[value].push(item);
      });

      return Object.entries(groups).map(([value, groupItems]) => {
        const groupKey = parentKey ? `${parentKey}|||${value}` : value;
        const children = buildGroups(groupItems, columnIndex + 1, groupKey);
        
        // Calculate totals
        const isLeaf = Array.isArray(children) && children.length > 0 && !children[0]?.children;
        let totalQty = 0;
        let totalCost = 0;
        let count = 0;
        
        if (isLeaf) {
          children.forEach(c => {
            totalQty += c.quantity || 0;
            totalCost += c.total_cost || 0;
            count += c._count || 1;
          });
        } else if (Array.isArray(children)) {
          children.forEach(c => {
            totalQty += c.totalQty || 0;
            totalCost += c.totalCost || 0;
            count += c.count || 0;
          });
        }
        
        return {
          key: groupKey,
          value,
          column: colKey,
          columnLabel: colDef?.label || colKey,
          count,
          totalQty,
          totalCost,
          children
        };
      });
    };

    return buildGroups(data.items, 0);
  }, [data.items, groupByColumns, visibleColumns]);

  // Render grouped rows
  const renderGroupedRows = (groups, level = 0) => {
    return groups.flatMap(group => {
      const isExpanded = expandedGroups[group.key] !== false;
      const hasChildren = Array.isArray(group.children) && group.children.length > 0;
      const isLeafGroup = hasChildren && !group.children[0]?.key;

      const rows = [
        <TableRow key={`group-${group.key}`} className="bg-[#e8dfd0]/70 hover:bg-[#e8dfd0]">
          <TableCell colSpan={visibleColumns.length} className="py-1">
            <button
              onClick={() => toggleGroup(group.key)}
              className="flex items-center gap-1 text-[10px] font-semibold text-[#1a1916]"
              style={{ paddingLeft: `${level * 20}px` }}
            >
              {isExpanded ? <ChevronDown size={12} /> : <ChevronRightIcon size={12} />}
              <span className="text-[#8a7e70]">{group.columnLabel}:</span>
              <span className="font-bold">{group.value}</span>
              <span className="ml-3 text-[#8a7e70] font-normal">
                ({group.count} hareket, {group.totalQty.toFixed(2)} adet, {group.totalCost.toFixed(2)} TL)
              </span>
            </button>
          </TableCell>
        </TableRow>
      ];

      if (isExpanded) {
        if (isLeafGroup) {
          rows.push(...group.children.map((m, idx) => renderAggregatedRow(m, level + 1, idx)));
        } else if (hasChildren) {
          rows.push(...renderGroupedRows(group.children, level + 1));
        }
      }

      return rows;
    });
  };

  const renderAggregatedRow = (m, indent = 0, idx = 0) => {
    return (
      <TableRow key={`agg-${idx}-${m.product_code}`} className="border-[#d0c4b0]">
        {visibleColumns.map(col => {
          if (col.key === 'created_at') {
            return (
              <TableCell key={col.key} className="text-[10px] text-[#3F3E39] whitespace-nowrap py-1" style={{ paddingLeft: indent ? `${indent * 20}px` : undefined }}>
                {col.getValue ? col.getValue(m) : m[col.key]}
              </TableCell>
            );
          }
          if (col.key === 'movement_type') {
            return (
              <TableCell key={col.key} className="py-1">
                <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${typeColors[m.movement_type] || 'bg-[#e8dfd0] text-[#3F3E39]'}`}>
                  {typeLabels[m.movement_type] || m.movement_type}
                </span>
              </TableCell>
            );
          }
          if (col.key === 'product_code') {
            return <TableCell key={col.key} className="font-mono text-[9px] text-[#8a7e70] py-1">{m.product_code}</TableCell>;
          }
          if (col.key === 'product_name') {
            return <TableCell key={col.key} className="text-[10px] font-medium text-[#1a1916] py-1">{m.product_name}</TableCell>;
          }
          if (col.key === 'quantity') {
            return (
              <TableCell key={col.key} className={`text-right text-[10px] font-semibold py-1 ${m.quantity >= 0 ? 'text-[#3d6e4e]' : 'text-[#7a3535]'}`}>
                {m.quantity > 0 ? '+' : ''}{m.quantity?.toFixed(2)}
                {m._count > 1 && <span className="text-[8px] text-[#8a7e70] ml-1">({m._count})</span>}
              </TableCell>
            );
          }
          if (col.key === 'unit') {
            return <TableCell key={col.key} className="text-[9px] text-[#8a7e70] py-1">{m.unit}</TableCell>;
          }
          if (col.key === 'unit_cost') {
            return <TableCell key={col.key} className="text-right font-mono text-[10px] text-[#3F3E39] py-1">{m.unit_cost?.toFixed(2)}</TableCell>;
          }
          if (col.key === 'total_cost') {
            return <TableCell key={col.key} className="text-right font-mono text-[10px] text-[#3F3E39] py-1">{m.total_cost?.toFixed(2)}</TableCell>;
          }
          if (col.key === 'notes') {
            return <TableCell key={col.key} className="text-[9px] text-[#8a7e70] max-w-[100px] truncate py-1">{m.notes || '-'}</TableCell>;
          }
          if (col.key === 'created_by_name') {
            return <TableCell key={col.key} className="text-[9px] text-[#8a7e70] py-1">{m.created_by_name || '-'}</TableCell>;
          }
          // Default
          const value = col.getValue ? col.getValue(m) : m[col.key];
          return <TableCell key={col.key} className="text-[10px] text-[#3F3E39] py-1">{value || '-'}</TableCell>;
        })}
      </TableRow>
    );
  };

  const renderNormalRow = (m) => (
    <TableRow key={m.id} className="border-[#d0c4b0]">
      <TableCell className="text-[10px] text-[#3F3E39] whitespace-nowrap py-1">
        {new Date(m.movement_date || m.created_at).toLocaleDateString('tr-TR')}
      </TableCell>
      <TableCell className="py-1">
        <span className={`px-1.5 py-0.5 rounded text-[9px] font-medium ${typeColors[m.movement_type] || 'bg-[#e8dfd0] text-[#3F3E39]'}`}>
          {m.movement_type_label || typeLabels[m.movement_type] || m.movement_type}
        </span>
      </TableCell>
      <TableCell className="font-mono text-[9px] text-[#8a7e70] py-1">{m.product_code}</TableCell>
      <TableCell className="text-[10px] font-medium text-[#1a1916] py-1">{m.product_name}</TableCell>
      <TableCell className="text-[10px] text-[#3F3E39] py-1">{m.category_name || '-'}</TableCell>
      <TableCell className="text-[10px] text-[#8a7e70] py-1">{m.subcategory_name || '-'}</TableCell>
      <TableCell className={`text-right text-[10px] font-semibold py-1 ${m.quantity >= 0 ? 'text-[#3d6e4e]' : 'text-[#7a3535]'}`}>
        {m.quantity > 0 ? '+' : ''}{m.quantity?.toFixed(2)}
      </TableCell>
      <TableCell className="text-[9px] text-[#8a7e70] py-1">{m.unit}</TableCell>
      <TableCell className="text-right font-mono text-[10px] text-[#3F3E39] py-1">{m.unit_cost?.toFixed(2)}</TableCell>
      <TableCell className="text-right font-mono text-[10px] text-[#3F3E39] py-1">{Math.abs(m.total_cost || 0).toFixed(2)}</TableCell>
      <TableCell className="text-[10px] text-[#3F3E39] py-1">{m.warehouse_name}</TableCell>
      <TableCell className="text-[9px] text-[#8a7e70] max-w-[100px] truncate py-1">{m.notes || '-'}</TableCell>
      <TableCell className="text-[9px] text-[#8a7e70] py-1">{m.created_by_name || '-'}</TableCell>
    </TableRow>
  );

  const totalPages = Math.ceil(data.total / perPage);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const exportParams = new URLSearchParams();
  if (typeFilter !== 'all') exportParams.set('movement_type', typeFilter);
  if (whFilter !== 'all') exportParams.set('warehouse_id', whFilter);
  if (catFilter !== 'all') exportParams.set('category_id', catFilter);
  if (subcatFilter !== 'all') exportParams.set('subcategory_id', subcatFilter);
  if (search) exportParams.set('search', search);
  if (dateFrom) exportParams.set('date_from', dateFrom);
  if (dateTo) exportParams.set('date_to', dateTo);
  const exportQs = exportParams.toString() ? `?${exportParams.toString()}` : '';

  const thClass = "text-[#1a1916] font-bold text-[9px] uppercase tracking-wider cursor-pointer select-none hover:bg-[#d0c4b0]/30 transition-colors py-1.5";

  return (
    <div data-testid="stock-movements-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1a1916] tracking-tight">Stok Hareketleri</h1>
          <p className="text-xs text-[#8a7e70]">{data.total} hareket</p>
        </div>
        <div className="flex gap-2">
          <a href={`${BACKEND}/api/erp/stock/movements/export/excel${exportQs}`} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" className="h-7 text-xs border-[#d0c4b0] text-[#3F3E39] hover:bg-[#e8dfd0]">
              <FileSpreadsheet size={12} className="mr-1" /> Excel
            </Button>
          </a>
          <a href={`${BACKEND}/api/erp/stock/movements/export/pdf${exportQs}`} target="_blank" rel="noreferrer">
            <Button variant="outline" size="sm" className="h-7 text-xs border-[#d0c4b0] text-[#3F3E39] hover:bg-[#e8dfd0]">
              <FileText size={12} className="mr-1" /> PDF
            </Button>
          </a>
          <Button onClick={() => setShowImport(true)} variant="outline" size="sm" className="h-7 text-xs border-[#d0c4b0] text-[#3F3E39] hover:bg-[#e8dfd0]">
            <Upload size={12} className="mr-1" /> Toplu Giris
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-3">
        <div className="relative flex-1 min-w-[150px] max-w-xs">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#8a7e70]" />
          <Input placeholder="Urun ara..." value={search} onChange={(e) => setSearch(e.target.value)}
            className="pl-7 h-7 text-xs bg-white border-[#d0c4b0]" />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-36 h-7 text-xs bg-white border-[#d0c4b0]">
            <SelectValue placeholder="Hareket Tipi" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tum Tipler</SelectItem>
            {Object.entries(typeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={whFilter} onValueChange={setWhFilter}>
          <SelectTrigger className="w-28 h-7 text-xs bg-white border-[#d0c4b0]">
            <SelectValue placeholder="Depo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tum Depolar</SelectItem>
            {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={catFilter} onValueChange={setCatFilter}>
          <SelectTrigger className="w-28 h-7 text-xs bg-white border-[#d0c4b0]">
            <SelectValue placeholder="Kategori" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tum Kategoriler</SelectItem>
            {parentCategories.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={subcatFilter} onValueChange={setSubcatFilter} disabled={catFilter === 'all'}>
          <SelectTrigger className={`w-28 h-7 text-xs bg-white border-[#d0c4b0] ${catFilter === 'all' ? 'opacity-50' : ''}`}>
            <SelectValue placeholder="Alt Kategori" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tumu</SelectItem>
            {filteredSubcats.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-28 h-7 text-xs bg-white border-[#d0c4b0]" />
        <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-28 h-7 text-xs bg-white border-[#d0c4b0]" />
      </div>

      {/* Grouping Area */}
      <div
        onDragOver={handleDragOver}
        onDrop={handleDropOnGroupArea}
        className={`mb-3 p-2 border-2 border-dashed rounded-lg transition-colors ${
          draggedColumn ? 'border-[#3d6e4e] bg-[#3d6e4e]/5' : 'border-[#d0c4b0] bg-[#faf7f2]'
        }`}
      >
        <div className="flex items-center gap-2 flex-wrap min-h-[28px]">
          <span className="text-[10px] text-[#8a7e70] font-medium">Grupla:</span>
          {groupByColumns.length === 0 ? (
            <span className="text-[10px] text-[#8a7e70] italic">Sutun basliklarini buraya surukleyin (gruplanan sutunlar tablodan kalkar, satirlar toplanir)</span>
          ) : (
            groupByColumns.map((col, idx) => {
              const colDef = allColumns.find(c => c.key === col);
              return (
                <div key={col} className="flex items-center gap-1 px-2 py-0.5 bg-[#2e2d29] text-[#f5f0e8] rounded text-[10px]">
                  {idx > 0 && <span className="text-[#8a7e70] mr-1">→</span>}
                  <span>{colDef?.label || col}</span>
                  <button onClick={() => removeGroupColumn(col)} className="ml-1 hover:text-red-300">
                    <X size={10} />
                  </button>
                </div>
              );
            })
          )}
          {groupByColumns.length > 0 && (
            <button onClick={() => { setGroupByColumns([]); setExpandedGroups({}); }} className="text-[9px] text-[#7a3535] hover:underline ml-2">
              Temizle
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              {groupByColumns.length === 0 ? (
                // Show all columns when not grouping
                <>
                  <TableHead className={thClass} onClick={() => toggleSort('created_at')} draggable={true} onDragStart={(e) => handleDragStart(e, 'created_at')}>
                    <div className="flex items-center"><GripVertical size={10} className="mr-0.5 text-[#8a7e70]" />Tarih<SortIcon col="created_at" /></div>
                  </TableHead>
                  <TableHead className={thClass} onClick={() => toggleSort('movement_type')} draggable={true} onDragStart={(e) => handleDragStart(e, 'movement_type')}>
                    <div className="flex items-center"><GripVertical size={10} className="mr-0.5 text-[#8a7e70]" />Hareket Tipi<SortIcon col="movement_type" /></div>
                  </TableHead>
                  <TableHead className={thClass}>Urun Kodu</TableHead>
                  <TableHead className={thClass} draggable={true} onDragStart={(e) => handleDragStart(e, 'product_name')}>
                    <div className="flex items-center"><GripVertical size={10} className="mr-0.5 text-[#8a7e70]" />Urun Adi</div>
                  </TableHead>
                  <TableHead className={thClass} draggable={true} onDragStart={(e) => handleDragStart(e, 'category_name')}>
                    <div className="flex items-center"><GripVertical size={10} className="mr-0.5 text-[#8a7e70]" />Kategori</div>
                  </TableHead>
                  <TableHead className={thClass} draggable={true} onDragStart={(e) => handleDragStart(e, 'subcategory_name')}>
                    <div className="flex items-center"><GripVertical size={10} className="mr-0.5 text-[#8a7e70]" />Alt Kategori</div>
                  </TableHead>
                  <TableHead className={`${thClass} text-right`} onClick={() => toggleSort('quantity')}>
                    <div className="flex items-center justify-end">Miktar<SortIcon col="quantity" /></div>
                  </TableHead>
                  <TableHead className={thClass}>Birim</TableHead>
                  <TableHead className={`${thClass} text-right`} onClick={() => toggleSort('unit_cost')}>
                    <div className="flex items-center justify-end">B.Maliyet<SortIcon col="unit_cost" /></div>
                  </TableHead>
                  <TableHead className={`${thClass} text-right`} onClick={() => toggleSort('total_cost')}>
                    <div className="flex items-center justify-end">T.Maliyet<SortIcon col="total_cost" /></div>
                  </TableHead>
                  <TableHead className={thClass} draggable={true} onDragStart={(e) => handleDragStart(e, 'warehouse_name')}>
                    <div className="flex items-center"><GripVertical size={10} className="mr-0.5 text-[#8a7e70]" />Depo</div>
                  </TableHead>
                  <TableHead className={thClass}>Notlar</TableHead>
                  <TableHead className={thClass}>Kaydeden</TableHead>
                </>
              ) : (
                // Show only visible columns when grouping
                visibleColumns.map(col => {
                  const isGroupable = col.groupable;
                  const isSortable = ['created_at', 'movement_type', 'quantity', 'unit_cost', 'total_cost'].includes(col.key);
                  const isNumeric = ['quantity', 'unit_cost', 'total_cost'].includes(col.key);
                  
                  return (
                    <TableHead 
                      key={col.key}
                      className={`${thClass} ${isNumeric ? 'text-right' : ''}`}
                      onClick={isSortable ? () => toggleSort(col.key) : undefined}
                      draggable={isGroupable}
                      onDragStart={isGroupable ? (e) => handleDragStart(e, col.key) : undefined}
                    >
                      <div className={`flex items-center ${isNumeric ? 'justify-end' : ''}`}>
                        {isGroupable && <GripVertical size={10} className="mr-0.5 text-[#8a7e70]" />}
                        {col.label}
                        {isSortable && <SortIcon col={col.key} />}
                      </div>
                    </TableHead>
                  );
                })
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {groupedData ? (
              renderGroupedRows(groupedData)
            ) : (
              data.items.map((m) => renderNormalRow(m))
            )}
            {data.items.length === 0 && (
              <TableRow><TableCell colSpan={visibleColumns.length || 13} className="text-center py-6 text-[10px] text-[#8a7e70]">Hareket bulunamadi</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center gap-2 text-[10px] text-[#8a7e70]">
          <span>Sayfa basina:</span>
          <Select value={String(perPage)} onValueChange={(v) => { setPerPage(Number(v)); setPage(1); }}>
            <SelectTrigger className="w-16 h-6 text-[10px] bg-white border-[#d0c4b0]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
              <SelectItem value="200">200</SelectItem>
              <SelectItem value="500">500</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-[#8a7e70]">
            {data.total > 0 ? `${(page - 1) * perPage + 1}-${Math.min(page * perPage, data.total)} / ${data.total}` : '0'}
          </span>
          <Button variant="outline" size="icon" className="h-6 w-6 border-[#d0c4b0]"
            disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
            <ChevronLeft size={12} />
          </Button>
          <Button variant="outline" size="icon" className="h-6 w-6 border-[#d0c4b0]"
            disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
            <ChevronRight size={12} />
          </Button>
        </div>
      </div>

      <ImportDialog open={showImport} onOpenChange={setShowImport} config={{
        title: 'Toplu Stok Girisi',
        templateUrl: '/api/erp/import/template/stock',
        previewUrl: '/import/stock/preview',
        executeUrl: '/import/stock/execute',
        columns: stockImportColumns,
        onComplete: fetchMovements
      }} />
    </div>
  );
}
